import { create } from "zustand";
import AuthenticateDto from "../dtos/authenticateDto";
import CurrentUserType from "../models/currentUserType";
import { mountStoreDevtool } from "simple-zustand-devtools";

interface UserQuery {
  currentToken?: string;
  currentUser?: CurrentUserType;
  useAuthenticate: boolean;
  authenticateId?: string;
  showLogin: boolean;
  showAuthenticate: boolean;
  userError?: string;
  loginDetails?: AuthenticateDto;
  showMessages: boolean;
  displayDialog: boolean;
}

interface UserQueryStore {
  userQuery: UserQuery;
  logoff: () => void;
  setUserError: (userError: string | undefined) => void;
  setLoginDetails: (newLoginDetails: AuthenticateDto | undefined) => void;
  setCurrentUser: (newCurrentUser: CurrentUserType | undefined) => void;
  setShowLogin: (showLogin: boolean) => void;
  setAuthenticateId: (authenticateId: string | undefined) => void;
  setShowAuthenticate: (showAuthenticate: boolean) => void;
  setUseAuthenticate: (useAuthenticate: boolean) => void;
  expiredToken: () => void;
  setDisplayDialog: (displayDialog: boolean) => void;
}

const useUserQueryStore = create<UserQueryStore>((set) => ({
  userQuery: {
    showLogin: true,
    showAuthenticate: false,
    useAuthenticate: false,
    displayDialog: false,
    showMessages: false,
  },
  logoff: () =>
    set((store) => ({
      userQuery: {
        ...store.userQuery,
        currentToken: undefined,
        currentUser: undefined,
        loginDetails: undefined,
        showLogin: true,
        displayDialog: false,
      },
    })),
  setUserError: (userError: string | undefined) => set((store) => ({ userQuery: { ...store.userQuery, userError } })),
  setLoginDetails: (newLoginDetails: AuthenticateDto | undefined) =>
    set((store) => ({
      userQuery: { ...store.userQuery, loginDetails: newLoginDetails, currentToken: newLoginDetails?.token },
    })),
  setCurrentUser: (newCurrentUser: CurrentUserType | undefined) =>
    set((store) => ({
      userQuery: {
        ...store.userQuery,
        currentUser: newCurrentUser,
        showMessages: !!(
          newCurrentUser?.active &&
          newCurrentUser?.extraInformation &&
          newCurrentUser?.extraInformation.length > 0 &&
          newCurrentUser.extraInformation.find((x) => x.key === "ShowMessages" && x.value === "true")
        ),
        showLogin: false,
        showAuthenticate: false,
        useAuthenticate: false,
        displayDialog: false,
      },
    })),
  setShowLogin: (showLogin: boolean) => set((store) => ({ userQuery: { ...store.userQuery, showLogin } })),
  setAuthenticateId: (authenticateId: string | undefined) =>
    set((store) => ({ userQuery: { ...store.userQuery, authenticateId } })),
  setShowAuthenticate: (showAuthenticate: boolean) =>
    set((store) => ({ userQuery: { ...store.userQuery, showAuthenticate } })),
  setUseAuthenticate: (useAuthenticate: boolean) =>
    set((store) => ({ userQuery: { ...store.userQuery, useAuthenticate } })),
  expiredToken: () =>
    set((store) => ({
      userQuery: { ...store.userQuery, showLogin: true, showAuthenticate: false, currentToken: undefined },
    })),
  setDisplayDialog: (displayDialog: boolean) => set((store) => ({ userQuery: { ...store.userQuery, displayDialog } })),
}));

if (process.env.NODE_ENV === "development") {
  mountStoreDevtool("User Store", useUserQueryStore);
}

export default useUserQueryStore;
